import { useState } from "react";
import SectionHeading from "../components/SectionHeading";
import { MaskedLines, FadeIn, Reveal } from "../components/motion/Reveal";
import PageMeta from "../components/PageMeta";

const APPAREL = [
  ["XS", "34", "26", "28"],
  ["S", "36", "27", "30"],
  ["M", "38", "28", "32"],
  ["L", "40", "29", "34"],
  ["XL", "42", "30", "36"],
  ["XXL", "44", "31", "38"],
];

const PADDLES = [
  ["Standard", '15.75"', '7.8"', "7.8–8.2 oz", '4.25"'],
  ["Elongated", '16.5"', '7.5"', "8.0–8.4 oz", '4.25"'],
  ["Widebody", '15.5"', '8.2"', "7.6–8.0 oz", '4.125"'],
];

const BALLS = [
  ["Outdoor", "40 holes", "74 mm", "26 g", "Hard compound"],
  ["Indoor", "26 holes", "74 mm", "23 g", "Soft compound"],
];

const TABS = [
  { id: "apparel", label: "Apparel", head: ["Size", "Chest (in)", "Length (in)", "Waist (in)"], rows: APPAREL },
  { id: "paddles", label: "Paddles", head: ["Shape", "Length", "Width", "Weight", "Grip"], rows: PADDLES },
  { id: "balls", label: "Balls", head: ["Type", "Holes", "Diameter", "Weight", "Compound"], rows: BALLS },
];

const SizeChart = () => {
  const [tab, setTab] = useState("apparel");
  const active = TABS.find((t) => t.id === tab);

  return (
    <div data-testid="size-chart-page">
      <PageMeta
        title="Size Chart & Equipment Specifications"
        description="Official sizing guides for pickleball apparel, paddles dimensions, and ball specifications."
      />
      <section className="border-b border-black/10 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 pb-16 pt-16 md:pt-24 lg:px-10">
          <FadeIn>
            <p className="mono-caption mb-8 text-neutral-500">Sizing Reference</p>
          </FadeIn>
          <h1 className="font-display text-4xl font-semibold leading-[1.02] tracking-tighter sm:text-5xl lg:text-6xl">
            <MaskedLines lines={["SIZE CHART", <span key="s" className="text-neutral-300">PRECISION MEASURED.</span>]} />
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1100px] px-5 py-20 md:py-28 lg:px-10">
        <SectionHeading number="01" title="Select Product Type" />
        <div className="flex flex-wrap gap-2" data-testid="size-chart-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`size-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`border px-5 py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] transition-colors ${
                tab === t.id ? "border-brand-ink bg-brand-ink text-white" : "border-black/15 text-neutral-500 hover:border-brand-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <Reveal key={tab} className="mt-10">
          <div className="overflow-x-auto border border-black/10" data-testid="size-chart-table">
            <table className="w-full min-w-[560px] font-mono text-sm">
              <thead>
                <tr className="bg-brand-ink text-white">
                  {active.head.map((h) => (
                    <th key={h} className="px-5 py-4 text-left text-[11px] uppercase tracking-[0.2em]">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-black/10">
                {active.rows.map((row) => (
                  <tr key={row[0]} className="transition-colors hover:bg-brand-smoke">
                    {row.map((cell, i) => (
                      <td key={i} className={`px-5 py-4 ${i === 0 ? "font-bold text-brand-red" : ""}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        <Reveal className="mt-10 flex flex-wrap items-center gap-6">
          <p className="max-w-md text-sm text-neutral-400">
            Custom sizing available on all apparel and paddle programs. Note your requirements in the inquiry form.
          </p>
        </Reveal>
      </section>
    </div>
  );
};

export default SizeChart;
